

import React, { useState, useEffect } from 'react';
import { useAuth } from 'auth/Auth';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Box,
  TextField,
  MenuItem,
  FormControl,
  Select,
  Card,
  Snackbar,
  Alert,
  Button,
  Autocomplete,
} from '@mui/material';
import {
  Close as CloseIcon,
  Edit as EditIcon,
  Phone as PhoneIcon,
  Group as GroupIcon,
  Email as EmailIcon,
  Assignment as TaskIcon,
  Event as EventIcon,
} from '@mui/icons-material';
import { Lock as LockIcon } from '@mui/icons-material';
import PropTypes from 'prop-types';
import colors from "assets/theme/base/colors";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton"; // Added MDButton import
import TimelineItem from "examples/Timeline/TimelineItem";
import { useTheme } from "@mui/material/styles";
import { v2 as t, v2Tones } from "styles/tokens/v2";
import { formatDate, formatTimeForDisplay, formatLocalDateTimeInput, formatLocalDateInput, toLocalDateTimeApi, normalizeTimeToHHMMSS } from "utils/DateTimeConversion";
import { REMINDER_OPTIONS, reminderAtFor } from "layouts/callcenter/followUp";
import { ArrowUpward } from '@mui/icons-material';
import { Stack } from '@mui/material';
import { FormHelperText } from '@mui/material';
import { CircularProgress } from '@mui/material';


const getDefaultDueDate = () => {
  const date = new Date();
  date.setDate(date.getDate() + 7);
  date.setHours(0, 0, 0, 0);
  return formatLocalDateTimeInput(date);
};

/** Match Select / Autocomplete control height in Activity Details edit mode. */
const activityFieldSx = {
  '& .MuiInputBase-root': {
    minHeight: '35px',
    fontSize: '0.875rem',
  },
  '& .MuiInputBase-input': {
    fontSize: '0.875rem',
    padding: '8px 12px',
    boxSizing: 'border-box',
  },
};


// Add this utility function at the top of your component
const formatDateForInput = (dateString, type) => {
  if (!dateString) return '';

  if (type === 'Meeting') {
    return formatLocalDateInput(dateString);
  }
  return formatLocalDateTimeInput(dateString);
};

// ActivityDetails Component
const ActivityDetails = ({ activity, open, onClose, onEdit, onNotify, isLocked }) => {
  const { axiosInstance } = useAuth()
  const [isEditMode, setIsEditMode] = useState(false);
  // const [editedActivity, setEditedActivity] = useState(null);
  const [editedActivity, setEditedActivity] = useState({
    id: activity?.id || null,
    title: activity?.title || '',
    type: activity?.type || '',
    status: activity?.status || 'Scheduled',
    description: activity?.description || '',
    due_date: activity?.due_date ? formatDateForInput(activity.due_date, activity.type) : getDefaultDueDate(),
    starttime: activity?.starttime || '',
    endtime: activity?.endtime || '',
    assigned_to: activity?.assigned_to || null,
    assigned_to_name: activity?.assigned_to_name || '',
    parent_table_id: activity?.parent_table_id || null,
    parent_table: activity?.parent_table || '',
  });
  const [users, setUsers] = useState([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  // Reminder edit: 'keep' leaves activity.reminder_at as it is.
  const [reminderChoice, setReminderChoice] = useState('keep');
  useEffect(() => {
    setReminderChoice('keep');
  }, [activity?.id, isEditMode]);
  // Add at the top with other state declarations
  const [validationErrors, setValidationErrors] = useState({
    due_date: '',
    status: '',
    title: '',
    starttime: '',
    endtime: '',
  });

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (isEditMode && users.length === 0) fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode]);

  const fetchUsers = async () => {
    setIsLoadingUsers(true);
    try {
      const response = await axiosInstance.get('/users/');
      if (response.data) {
        setUsers(response.data);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setIsLoadingUsers(false);
    }
  };

  useEffect(() => {
    if (activity) {
      setValidationErrors({ due_date: '', status: '', title: '', starttime: '', endtime: '' });
      setEditedActivity({
        id: activity.id,
        title: activity.title || '',
        type: activity.type || '',
        status: activity.status || 'Scheduled',
        description: activity.description || '',
        due_date: formatDateForInput(activity.due_date, activity.type),
        starttime: activity.starttime || '',
        endtime: activity.endtime || '',
        assigned_to: activity.assigned_to || null,
        assigned_to_name: activity.assigned_to_name || '',
        parent_table_id: activity.parent_table_id || null,
        parent_table: activity.parent_table || '',
      });
    }
  }, [activity]);

  // Add validation function
  const validateDateStatus = (date, status) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selectedDate = new Date(date);
    selectedDate.setHours(0, 0, 0, 0);

    let errors = {
      due_date: '',
      status: ''
    };

    if (status === 'Scheduled') {
      if (selectedDate < today) {
        errors.due_date = 'Cannot set past date for scheduled activities';
        errors.status = 'Status cannot be scheduled for past dates';
      }
    }

    setValidationErrors((prev) => ({ ...prev, ...errors }));
    return !errors.due_date && !errors.status;
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  const validateRequiredFields = () => {
    const errors = { title: '', starttime: '', endtime: '' };

    if (!editedActivity.title?.trim()) {
      errors.title = 'Title is required';
    }

    if (editedActivity.type === 'Meeting') {
      const start = (editedActivity.starttime || '').slice(0, 5);
      const end = (editedActivity.endtime || '').slice(0, 5);

      if (!start) errors.starttime = 'Start time is required';
      if (!end) errors.endtime = 'End time is required';
      if (start && end && end <= start) {
        errors.endtime = 'End time must be after start time';
      }
    }

    setValidationErrors((prev) => ({ ...prev, ...errors }));
    return !errors.title && !errors.starttime && !errors.endtime;
  };

  // Modify handleSave function
  // Update the handleSave function to ensure proper date format before saving
  // Update the handleSave function
  const handleSave = async () => {
    const dateOk = validateDateStatus(editedActivity.due_date, editedActivity.status);
    const fieldsOk = validateRequiredFields();

    if (!dateOk || !fieldsOk) {
      setSnackbar({
        open: true,
        message: 'Please correct the highlighted fields',
        severity: 'error'
      });
      return;
    }

    setIsSaving(true);

    try {
      // Prepare the activity data for the API
      const activityData = {
        id: editedActivity.id,
        title: editedActivity.title,
        type: editedActivity.type,
        status: editedActivity.status,
        description: editedActivity.description,
        due_date: editedActivity.type === 'Meeting'
          ? formatLocalDateInput(editedActivity.due_date)
          : toLocalDateTimeApi(editedActivity.due_date),
        starttime: editedActivity.type === 'Meeting' ? normalizeTimeToHHMMSS(editedActivity.starttime) : null,
        endtime: editedActivity.type === 'Meeting' ? normalizeTimeToHHMMSS(editedActivity.endtime) : null,
        assigned_to: editedActivity.assigned_to,
        parent_table_id: editedActivity.parent_table_id,
        parent_table: editedActivity.parent_table,
      };
      if (reminderChoice !== 'keep') {
        // Changing the reminder re-arms it on the server (reminder_sent_at is cleared).
        activityData.reminder_at = editedActivity.status === 'Scheduled'
          ? reminderAtFor(
              editedActivity.type === 'Meeting'
                ? `${String(editedActivity.due_date).split('T')[0]}T${(editedActivity.starttime || '09:00').slice(0, 5)}`
                : formatLocalDateTimeInput(editedActivity.due_date),
              reminderChoice
            )
          : null;
      }

      // Call the API using PATCH method
      await axiosInstance.patch(`/activities/${activity.id}`, activityData);

      onNotify?.('Activity updated successfully!', 'success');

      if (onEdit) {
        onEdit({
          ...activity,
          ...activityData,
          assigned_to_name: editedActivity.assigned_to_name,
        });
      }

      // Close edit mode
      setIsEditMode(false);
      onClose();

    } catch (error) {
      console.error('Error editing activity:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.detail || 'Error updating activity',
        severity: 'error'
      });
    } finally {
      setIsSaving(false); // Stop loading
    }
  };


  const handleCancel = () => {
    setEditedActivity({ ...activity });
    setIsEditMode(false);
    onClose(); // Close the dialog
  };

  const handleDialogClose = () => {
    if (isEditMode && !isLocked) {
      setSnackbar({
        open: true,
        message: 'You have unsaved changes. Do you want to discard them?',
        severity: 'warning',
        action: (
          <>
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                setIsEditMode(false);
                setEditedActivity({ ...activity });
                onClose();
                setSnackbar({ ...snackbar, open: false });
              }}
            >
              Yes
            </Button>
            <Button
              color="inherit"
              size="small"
              onClick={() => setSnackbar({ ...snackbar, open: false })}
            >
              No
            </Button>
          </>
        )
      });
    } else {
      setIsEditMode(false);
      setEditedActivity({ ...activity });
      onClose();
    }
  };

  const isV2 = useTheme()?.pearlUi === "v2";
  if (!activity || !editedActivity) return null;

  const activityTypes = ['Call', 'Meeting', 'Email', 'Task', 'Other'];
  const activityStatuses = ['Scheduled', 'Completed', 'Cancelled'];

  const getActivityIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'call':
        return <PhoneIcon />;
      case 'meeting':
        return <GroupIcon />;
      case 'email':
        return <EmailIcon />;
      case 'task':
        return <TaskIcon />;
      default:
        return <EventIcon />;
    }
  };

  const getActivityColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return 'success';
      case 'scheduled':
        return 'info';
      case 'cancelled':
        return 'error';
      default:
        return 'info';
    }
  };


  // Update the handleTypeChange function to handle date format changes
  const handleTypeChange = (e) => {
    const newType = e.target.value;
    const currentDate = editedActivity.due_date;

    setEditedActivity(prev => ({
      ...prev,
      type: newType,
      starttime: newType === 'Meeting' ? '09:00' : null, // Set default start time
      endtime: newType === 'Meeting' ? '10:00' : null, // Set default end time
      due_date: formatDateForInput(currentDate, newType)
    }));
  };

  const handleStartTimeChange = (e) => {
    const startTime = e.target.value;

    // Calculate end time (30 minutes later)
    let [hours, minutes] = startTime.split(':').map(Number);
    minutes += 30;
    if (minutes >= 60) {
      hours += 1;
      minutes -= 60;
    }
    if (hours > 23) hours = 23;

    const endTime = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;

    setEditedActivity(prev => ({
      ...prev,
      starttime: startTime,
      endtime: endTime
    }));
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleDialogClose}
        PaperProps={{
          sx: {
            width: '360px',
            maxHeight: '80vh',
            borderRadius: '8px'
          }
        }}
      >
        <DialogTitle className="pearl-popup-header"
          sx={{
            p: 0,
            backgroundColor: colors.popupHeader.background,
            borderTopLeftRadius: 8,
            borderTopRightRadius: 8,
          }}
        >
          <Box 
            display="flex" 
            justifyContent="space-between" 
            alignItems="center"
            px={4}
            py={3}
          >
            <Typography 
              variant="h6" 
              sx={{ 
                fontSize: '1rem', 
                fontWeight: 600,
                color: '#FFFFFF'
              }}
            >
              Activity Details
            </Typography>
            <IconButton 
              onClick={handleDialogClose} 
              size="small"
              sx={{ color: '#FFFFFF' }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 1.5 }}>
          <Stack spacing={1.5} sx={{ mt: 2 }}>
            {/* Type */}
            <Box>
              <Typography variant="caption" sx={{ mb: 0.5, fontWeight: 500, color: 'text.secondary' }}>
                Activity Type
              </Typography>
              {isEditMode ? (
                <Select
                  fullWidth
                  value={editedActivity.type}
                  size="small"
                  sx={{
                    height: '35px',
                    fontSize: '0.875rem',
                    '& .MuiSelect-select': {
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }
                  }}
                  onChange={handleTypeChange}
                >
                  {activityTypes.map((type) => (
                    <MenuItem key={type} value={type} sx={{ fontSize: '0.875rem' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {getActivityIcon(type)}
                        <span>{type}</span>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              ) : (
                <Box sx={{
                  mt: 0.5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  fontSize: '0.875rem'
                }}>
                  {getActivityIcon(activity.type)}
                  <Typography sx={{ fontSize: '0.875rem' }}>{activity.type}</Typography>
                </Box>
              )}
            </Box>

            {/* Title */}
            <Box>
              <Typography variant="caption" sx={{ mb: 0.5, fontWeight: 500, color: 'text.secondary' }}>
                Title
              </Typography>
              {isEditMode ? (
                  <TextField
                      fullWidth
                      size="small"
                      value={editedActivity.title || ''}
                      onChange={(e) => {
                        setEditedActivity({ ...editedActivity, title: e.target.value });
                        if (validationErrors.title) setValidationErrors((prev) => ({ ...prev, title: '' }));
                      }}
                      error={!!validationErrors.title}
                      helperText={validationErrors.title}
                      placeholder="Enter title"
                      sx={activityFieldSx}
                  />
              ) : (
                <Typography
                  sx={{
                    mt: 0.5,
                    fontSize: '0.875rem',
                    color: 'text.primary'
                  }}
                >
                  {activity.title || 'No title'}
                </Typography>
              )}
            </Box>

            {/* Assigned To */}
            <Box>
              <Typography variant="caption" sx={{ mb: 0.5, fontWeight: 500, color: 'text.secondary' }}>
                Assigned To
              </Typography>
              {isEditMode ? (
                <Autocomplete
                  fullWidth
                  loading={isLoadingUsers}
                  options={users}
                  getOptionLabel={(option) => option.fullname || ''}
                  value={users.find(user => user.id === editedActivity.assigned_to) || null}
                  onChange={(event, newValue) => {
                    setEditedActivity({
                      ...editedActivity,
                      assigned_to: newValue?.id || null,
                      assigned_to_name: newValue?.fullname || ''
                    });
                  }}
                  size="small"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      sx={activityFieldSx}
                    />
                  )}
                />
              ) : (
                <Typography
                  sx={{
                    mt: 0.5,
                    fontSize: '0.875rem',
                    color: 'text.primary'
                  }}
                >
                  {activity.assigned_to_name || 'Unassigned'}
                </Typography>
              )}
            </Box>

            {/* Reminder */}
            {(isEditMode || activity?.reminder_at) && (
              <Box>
                <Typography variant="caption" sx={{ mb: 0.5, fontWeight: 500, color: 'text.secondary' }}>
                  Reminder
                </Typography>
                {isEditMode ? (
                  <FormControl fullWidth>
                    <Select
                      fullWidth
                      size="small"
                      value={reminderChoice}
                      onChange={(e) => setReminderChoice(e.target.value)}
                      sx={{ height: '35px', fontSize: '0.875rem' }}
                      inputProps={{ 'aria-label': 'Reminder' }}
                    >
                      <MenuItem value="keep" sx={{ fontSize: '0.875rem' }}>
                        {activity?.reminder_at ? `Keep (${new Date(activity.reminder_at).toLocaleString()})` : 'Keep (none)'}
                      </MenuItem>
                      {REMINDER_OPTIONS.map((o) => (
                        <MenuItem key={o.value} value={o.value} sx={{ fontSize: '0.875rem' }}>{o.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : (
                  <Typography sx={{ mt: 0.5, fontSize: '0.875rem' }}>
                    {new Date(activity.reminder_at).toLocaleString()}
                    {activity.reminder_sent_at ? ' (sent)' : ''}
                  </Typography>
                )}
              </Box>
            )}

            {/* Status */}
            <Box>
              <Typography variant="caption" sx={{ mb: 0.5, fontWeight: 500, color: 'text.secondary' }}>
                Status
              </Typography>
              {isEditMode ? (
                <FormControl fullWidth error={!!validationErrors.status}>
                  <Select
                    fullWidth
                    value={editedActivity.status}
                    size="small"
                    sx={{
                      height: '35px',
                      fontSize: '0.875rem'
                    }}
                    onChange={(e) => {
                      const newStatus = e.target.value;
                      validateDateStatus(editedActivity.due_date, newStatus);
                      setEditedActivity({
                        ...editedActivity,
                        status: newStatus
                      });
                    }}
                  >
                    {activityStatuses.map((status) => (
                      <MenuItem key={status} value={status} sx={{ fontSize: '0.875rem' }}>
                        <Typography
                          sx={{
                            fontSize: '0.875rem',
                            color: getActivityColor(status) + '.main'
                          }}
                        >
                          {status}
                        </Typography>
                      </MenuItem>
                    ))}
                  </Select>
                  {validationErrors.status && (
                    <FormHelperText>{validationErrors.status}</FormHelperText>
                  )}
                </FormControl>
              ) : (
                <Typography
                  sx={{
                    mt: 0.5,
                    fontSize: '0.875rem',
                    color: getActivityColor(activity.status) + '.main',
                    fontWeight: 500,
                    display: 'inline-block',
                    backgroundColor: getActivityColor(activity.status) + '.lighter',
                    padding: '4px 12px',
                    borderRadius: '16px',
                    lineHeight: 1
                  }}
                >
                  {activity.status}
                </Typography>
              )}
            </Box>

            {/* Due Date */}
            <Box>
              <Typography variant="caption" sx={{ mb: 0.5, fontWeight: 500, color: 'text.secondary' }}>
                Due Date
              </Typography>
              {isEditMode ? (
                <TextField
                  fullWidth
                  size="small"
                  type={editedActivity.type === 'Meeting' ? "date" : "datetime-local"}
                  value={editedActivity.due_date}
                  onChange={(e) => {
                    const newDate = e.target.value;
                    validateDateStatus(newDate, editedActivity.status);
                    setEditedActivity({
                      ...editedActivity,
                      due_date: newDate
                    });
                  }}
                  error={!!validationErrors.due_date}
                  helperText={validationErrors.due_date}
                  sx={activityFieldSx}
                  InputLabelProps={{ shrink: true }}
                />
              ) : (
                <Typography
                  sx={{
                    mt: 0.5,
                    fontSize: '0.875rem',
                    color: 'text.primary'
                  }}
                >
                  {activity.type === 'Meeting'
                    ? formatDate(activity.due_date, 'date')
                    : formatDate(activity.due_date, 'datetime')}
                </Typography>
              )}
            </Box>

            {/* Meeting Time */}
            {editedActivity.type === 'Meeting' && (
              <Box>
                <Typography variant="caption" sx={{ mb: 0.5, fontWeight: 500, color: 'text.secondary' }}>
                  Meeting Time
                </Typography>
                {isEditMode ? (
                  <Stack spacing={1}>
                    <TextField
                        fullWidth
                        size="small"
                        type="time"
                        value={editedActivity.starttime || '09:00'}
                        onChange={(e) => {
                          handleStartTimeChange(e);
                          setValidationErrors((prev) => ({ ...prev, starttime: '', endtime: '' }));
                        }}
                        error={!!validationErrors.starttime}
                        helperText={validationErrors.starttime}
                        sx={activityFieldSx}
                    />
                    <TextField
                        fullWidth
                        size="small"
                        type="time"
                        value={editedActivity.endtime || '10:00'}
                        onChange={(e) => {
                          setEditedActivity((prev) => ({ ...prev, endtime: e.target.value }));
                          setValidationErrors((prev) => ({ ...prev, endtime: '' }));
                        }}
                        error={!!validationErrors.endtime}
                        helperText={validationErrors.endtime}
                        sx={activityFieldSx}
                    />
                  </Stack>
                ) : (
                  <Typography
                    sx={{
                      mt: 0.5,
                      fontSize: '0.875rem',
                      color: 'text.primary'
                    }}
                  >
                    {`${formatTimeForDisplay(activity.starttime)} - ${formatTimeForDisplay(activity.endtime)}`}
                  </Typography>
                )}
              </Box>
            )}

            {/* Description */}
            <Box>
              <Typography variant="caption" sx={{ mb: 0.5, fontWeight: 500, color: 'text.secondary' }}>
                Description
              </Typography>
              {isEditMode ? (
                <TextField
                  fullWidth
                  size="small"
                  multiline
                  rows={3}
                  value={editedActivity.description || ''}
                  onChange={(e) => setEditedActivity({
                    ...editedActivity,
                    description: e.target.value
                  })}
                  placeholder="Add description (optional)"
                  sx={{
                    '& .MuiInputBase-input': {
                      fontSize: '0.875rem'
                    }
                  }}
                />
              ) : (
                <Typography
                  sx={{
                    mt: 0.5,
                    fontSize: '0.875rem',
                    color: 'text.primary'
                  }}
                >
                  {activity.description || 'No description'}
                </Typography>
              )}
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions
            sx={{
              p: 3,
              borderTop: `1px solid ${isV2 ? t.border : '#e0e0e0'}`,
              backgroundColor: 'background.paper'
            }}
        >
          {isLocked ? (
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              color: 'text.secondary'
            }}>
              <LockIcon sx={{ fontSize: '0.875rem' }} />
              {/* <MDTypography variant="caption">
        View Only 
      </MDTypography> */}
              <MDTypography
                variant="caption"
                sx={{
                  fontWeight: 700,  // or 'bold'
                  // If you want it even bolder, you can use:
                  // fontWeight: 800,
                }}
              >
                View Only
              </MDTypography>
            </Box>
          ) : isEditMode ? (
            <>
              <MDButton
                variant="outlined"
                color="secondary"
                onClick={handleCancel}
                sx={{
                  minWidth: '80px',
                  minHeight: '32px',
                  mr: 1,
                  fontSize: '0.75rem',
                  padding: '0.25rem 1rem',
                }}
              >
                Cancel
              </MDButton>
              <MDButton
                variant="gradient"
                color="info"
                onClick={handleSave}
                disabled={isSaving}
                sx={{
                  minWidth: '80px',
                  minHeight: '32px',
                  fontSize: '0.75rem',
                  padding: '0.25rem 1rem',
                }}
              >
                {isSaving ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  'Save'
                )}
              </MDButton>
            </>
          ) : (
            <MDButton
              variant="gradient"
              color="info"
              onClick={() => setIsEditMode(true)}
              startIcon={<EditIcon sx={{ fontSize: '1rem' }} />}
              sx={{
                minHeight: '32px',
                fontSize: '0.75rem',
                padding: '0.25rem 1rem',
              }}
            >
              Edit
            </MDButton>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.action ? null : 6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          variant="filled"
          sx={{
            width: '100%',
            '& .MuiAlert-action': {
              alignItems: 'center',
            }
          }}
          action={snackbar.action}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};



function Activities({ activities = [], onDelete, onEdit, isLocked }) {
  const [localActivities, setLocalActivities] = useState(activities);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const isV2 = useTheme()?.pearlUi === "v2";

  // Sync from parent only when the *contents* change. Parents often pass
  // `activities.filter(Boolean)` (new array every render); resetting on every
  // identity change wipes optimistic edits.
  const activitiesSyncKey = Array.isArray(activities)
    ? activities.map((a) => `${a?.id ?? ""}:${a?.updated_at ?? a?.status ?? ""}`).join("|")
    : "";
  useEffect(() => {
    setLocalActivities(activities);
    // intentionally key-gated; activities is read when the key changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activitiesSyncKey]);

  const handleActivityUpdated = (updated) => {
    if (!updated?.id) return;
    setLocalActivities((prev) =>
      prev.map((a) => (a.id === updated.id ? { ...a, ...updated } : a)),
    );
    setSelectedActivity((prev) =>
      prev?.id === updated.id ? { ...prev, ...updated } : prev,
    );
    onEdit?.(updated);
  };
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  const showSnackbar = (message, severity = 'info') =>
      setSnackbar({ open: true, message, severity });

  // Add this near the other state declarations in ActivityDetails component

  const handleActivityClick = (activity) => {
    // Allow viewing the activity details even when locked
    setSelectedActivity(activity);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  const getActivityIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'call':
        return <PhoneIcon />;
      case 'meeting':
        return <GroupIcon />;
      case 'email':
        return <EmailIcon />;
      case 'task':
        return <TaskIcon />;
      default:
        return <EventIcon />;
    }
  };

  const getActivityColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return 'success';
      case 'scheduled':
        return 'info';
      case 'cancelled':
        return 'error';
      default:
        return 'info';
    }
  };

  const getActivityTitle = (activity) => {
    let title = `${activity.type} - ${activity.status}`;

    if (activity.type === 'Meeting' && activity.starttime && activity.endtime) {
      title += `\nTime: ${formatTimeForDisplay(activity.starttime)} - ${formatTimeForDisplay(activity.endtime)}`;
    }

    return title;
  };

  const todayActivities = localActivities.filter(activity => {
    const activityDate = new Date(activity.due_date);
    const today = new Date();
    return (
      activityDate.getDate() === today.getDate() &&
      activityDate.getMonth() === today.getMonth() &&
      activityDate.getFullYear() === today.getFullYear()
    );
  });

  return (
    <>
      <Card sx={{ height: "100%", boxShadow: 'none', ...(isV2 ? { border: 'none' } : {}) }}>
        <MDBox pt={isV2 ? (todayActivities.length > 0 ? 1 : 0) : 3} px={isV2 ? 1 : 3}>
          {todayActivities.length > 0 && (
            <MDTypography variant="h6" fontWeight="medium">
              Activity Timeline
              <MDTypography variant="button" color="text" fontWeight="regular">
                <MDTypography display="inline" variant="body2" verticalAlign="middle">
                  <Box component="span" color="success.main">
                    <ArrowUpward fontSize="small" />
                  </Box>
                </MDTypography>
                &nbsp;
                <MDTypography variant="button" color="text" fontWeight="medium">
                  ({todayActivities.length} {todayActivities.length === 1 ? 'activity' : 'activities'} today)
                </MDTypography>
              </MDTypography>
            </MDTypography>
          )}
        </MDBox>
        <MDBox p={2}>
          {localActivities.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                mt: -2,
                // py: 3,
                // px: 2,
              }}
            >
              <Box sx={{
                width: 50,
                height: 50,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'rgba(0,0,0,0.04)',
                mb: 1.5
              }}>
                <EventIcon sx={{ fontSize: 24, color: 'text.secondary' }} />
              </Box>
              <Typography variant="body2" color="text.secondary" align="center">
                No activities to display.
              </Typography>
              {/* <Typography variant="caption" color="text.secondary" align="center" sx={{ mt: 0.5 }}>
                Create your first activity by clicking the "Add Activity" button above.
              </Typography> */}
              {/* <Typography variant="caption" color="text.secondary" align="center" sx={{ mt: 1 }}>
                ({todayActivities.length} {todayActivities.length === 1 ? 'activity' : 'activities'} today)
              </Typography> */}
            </Box>
          ) : (
            <MDBox
              sx={{
                maxHeight: '400px',
                overflowY: 'auto',
                '&::-webkit-scrollbar': {
                  width: '6px',
                },
                '&::-webkit-scrollbar-track': {
                  background: '#f1f1f1',
                  borderRadius: '10px',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: '#888',
                  borderRadius: '10px',
                },
                '&::-webkit-scrollbar-thumb:hover': {
                  background: '#555',
                },
              }}
            >
              {isV2 && localActivities.map((activity, index) => {
                // v2 (design-reference "Sales · Deal record" Activity card):
                // icon, title and meta on the left, the date on the right.
                const status = activity.status?.toLowerCase();
                const statusColor = status === 'completed' ? v2Tones.success.fg : status === 'cancelled' ? v2Tones.danger.fg : t.text2;
                const meetingTime = activity.type === 'Meeting' && activity.starttime && activity.endtime
                  ? ` · ${formatTimeForDisplay(activity.starttime)} - ${formatTimeForDisplay(activity.endtime)}`
                  : '';
                return (
                  <Box
                    key={activity.id || index}
                    role="button"
                    tabIndex={0}
                    onClick={() => handleActivityClick(activity)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleActivityClick(activity); } }}
                    sx={{
                      display: 'flex', gap: 1.5, alignItems: 'flex-start', px: 1, py: 1.25, borderRadius: '8px', cursor: 'pointer',
                      '&:hover': { backgroundColor: t.hover },
                      '&:focus-visible': { outline: `2px solid ${t.link}`, outlineOffset: -2 },
                    }}
                  >
                    <Box sx={{ width: 32, height: 32, flexShrink: 0, borderRadius: '50%', display: 'grid', placeItems: 'center', bgcolor: t.surface2, border: `1px solid ${t.border}`, color: t.text2, '& svg': { fontSize: 17 } }}>
                      {getActivityIcon(activity.type)}
                    </Box>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <MDTypography component="div" sx={{ fontSize: 13, fontWeight: 600, lineHeight: 1.4, color: t.text, overflowWrap: 'break-word' }}>
                        {activity.title || activity.type}
                      </MDTypography>
                      <MDTypography component="div" sx={{ fontSize: 12, lineHeight: 1.4, color: t.text2 }}>
                        {activity.type} · <Box component="span" sx={{ color: statusColor, fontWeight: 600 }}>{activity.status}</Box>{meetingTime}
                      </MDTypography>
                    </Box>
                    <MDTypography component="div" sx={{ fontSize: 12, lineHeight: 1.4, color: t.text3, whiteSpace: 'nowrap' }}>
                      {formatDate(activity.due_date, 'date')}
                    </MDTypography>
                  </Box>
                );
              })}
              {!isV2 && localActivities.map((activity, index) => (
                <Box
                  key={activity.id || index}
                  onClick={() => handleActivityClick(activity)}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                    borderRadius: 1,
                    transition: 'background-color 0.2s',
                  }}
                >
                  <TimelineItem
                    color={getActivityColor(activity.status)}
                    icon={getActivityIcon(activity.type)}
                    title={
                      <MDTypography
                        variant="button"
                        fontWeight="medium"
                        color={getActivityColor(activity.status)}
                      >
                        {getActivityTitle(activity)}
                      </MDTypography>
                    }
                    dateTime={formatDate(activity.due_date, 'datetime')}
                  />
                </Box>
              ))}
            </MDBox>
          )}
        </MDBox>

        <ActivityDetails
          activity={selectedActivity}
          open={!!selectedActivity}
          onClose={() => setSelectedActivity(null)}
          onEdit={isLocked ? null : handleActivityUpdated}
          onNotify={showSnackbar}
          isLocked={isLocked}
        />
      </Card>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}

ActivityDetails.propTypes = {
  activity: PropTypes.shape({
    id: PropTypes.number,
    title: PropTypes.string,
    type: PropTypes.string,
    status: PropTypes.string,
    description: PropTypes.string,
    due_date: PropTypes.string,
    starttime: PropTypes.string,
    endtime: PropTypes.string,
    assigned_to: PropTypes.number,
    assigned_to_name: PropTypes.string,
    parent_table_id: PropTypes.number,
    parent_table: PropTypes.string,
  }),
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onNotify: PropTypes.func,
  onEdit: PropTypes.func,
  isLocked: PropTypes.bool,
};

export default Activities;